import { useEffect, useState } from "react";
import { Box, Chip, IconButton, InputAdornment, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Shared pieces — presentational and cache-update alike — for any screen that renders a
// WhatsApp thread — originally written once in WhatsAppInboxPage.jsx (the admin-wide Inbox)
// and duplicated a second time in CreatorWhatsAppTab.jsx (Payment History's per-creator
// tab). Pulled out here so a third screen (PlanWhatsAppPage, the plan-scoped view) doesn't
// need a third copy, and WhatsAppInboxPage now imports from here instead of keeping its own
// definitions.

// You can have multiple WhatsApp business numbers, same as multiple Instagram accounts —
// so each thread is colored by which of our numbers it's on. Numbers aren't known ahead of
// time, so the color is derived deterministically from the number itself rather than a
// hardcoded map.
const WA_PALETTE = ["#25D366", "#128C7E", "#075E54", "#34B7F1", "#00A884", "#008069", "#0F969C"];
export function getNumberColor(key) {
  const str = String(key || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return WA_PALETTE[hash % WA_PALETTE.length];
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function istDateKey(d) {
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  return `${ist.getUTCFullYear()}-${ist.getUTCMonth()}-${ist.getUTCDate()}`;
}

export function formatTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
}

export function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (istDateKey(d) === istDateKey(now)) return "Today";
  if (istDateKey(d) === istDateKey(new Date(now.getTime() - 86400000))) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

export function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
}

export function groupMessagesByDate(messages) {
  const groups = {};
  for (const msg of messages) {
    const label = formatDateLabel(msg.sentAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
  }
  return groups;
}

// The name resolution below already prefers influencer data, but the customer's actual
// WhatsApp number was never shown anywhere — neither the list nor the thread header — so
// there was no way to tell who a bare "customerName" fallback (or an unmatched conversation)
// actually was without opening WhatsApp itself. customerWaId is the one field guaranteed to
// exist on every conversation (it's the join key — see WhatsappConversation's schema
// comment), so it's the reliable source here, formatted the way a person would read it
// rather than as a raw digit string. Most numbers here are Indian (country code 91 + a
// 10-digit number = 12 digits); anything else falls back to a generic "assume the extra
// digits beyond 10 are the country code" split rather than guessing wrong.
export function formatWaId(waId) {
  const digits = String(waId || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length > 10) {
    const ccLen = digits.length - 10;
    return `+${digits.slice(0, ccLen)} ${digits.slice(ccLen)}`;
  }
  return `+${digits}`;
}

export function hasRealName(conv) {
  return Boolean(conv?.influencerDbId?.userName || conv?.influencerDbId?.username || conv?.customerName);
}

export function conversationDisplayName(conv) {
  return conv?.influencerDbId?.userName || conv?.influencerDbId?.username || conv?.customerName || formatWaId(conv?.customerWaId) || "Unknown";
}

// react-query cache updaters for a single thread's socket-driven live updates — identical
// regardless of which screen owns the query, since only the query key (scoped by the
// caller) differs between the admin Inbox and a plan-scoped thread.
export function upsertThreadMessage(oldData, conversationId, message) {
  if (!oldData?.conversation || oldData.conversation.conversationId !== conversationId) return oldData;
  const alreadyExists = oldData.conversation.messages.some((m) => m.waMessageId === message.waMessageId);
  if (alreadyExists) return oldData;
  return {
    ...oldData,
    conversation: {
      ...oldData.conversation,
      lastMessageAt: message.sentAt,
      messages: [...oldData.conversation.messages, message],
    },
  };
}

// Delivery-status ticks (sent -> delivered -> read) arrive as a separate socket event from
// the message itself — see whatsappWebhook.js's status handler, which emits
// 'whatsapp-message-status' rather than piggybacking on 'new-whatsapp-message'.
export function upsertThreadMessageStatus(oldData, conversationId, waMessageId, status) {
  if (!oldData?.conversation || oldData.conversation.conversationId !== conversationId) return oldData;
  return {
    ...oldData,
    conversation: {
      ...oldData.conversation,
      messages: oldData.conversation.messages.map((m) => (m.waMessageId === waMessageId ? { ...m, status } : m)),
    },
  };
}

// Delivery tick — WhatsApp-native visual language (single/double/blue check).
export function StatusTick({ status }) {
  if (!status) return null;
  if (status === "read") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "#53BDEB" }} />;
  if (status === "delivered") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  if (status === "sent") return <DoneRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  return null;
}

// Newest activity first — whoever sent the latest message (us or the creator), so the times on the
// rows ("now", "5m", "2h", "4d") read smallest to largest from the top. The API already returns
// this order, but lists are also edited in place by live socket updates and cache writes, so the
// pages sort them themselves instead of trusting arrival order. Ties (a bulk send stamps many chats in
// the same second) fall back to the conversation id so the order never flickers between renders.
const newestActivityAt = (conv) =>
  Math.max(
    new Date(conv.lastMessageAt || 0).getTime() || 0,
    new Date((conv.messages || [])[0]?.sentAt || 0).getTime() || 0
  );

export function sortConversationsNewestFirst(conversations) {
  return [...conversations].sort(
    (a, b) => newestActivityAt(b) - newestActivityAt(a) || String(a.conversationId).localeCompare(String(b.conversationId))
  );
}

// Optimistic read flip for one chat in a cached list response ({ conversations, unreadCount }) — the
// chat un-bolds, its green message count clears and the Unread badge drops instantly; the server's
// answer replaces it a moment later.
export function setConversationUnread(oldData, conversationId, unread) {
  if (!oldData?.conversations) return oldData;
  const target = oldData.conversations.find((c) => c.conversationId === conversationId);
  if (!target || Boolean(target.unread) === unread) return oldData;
  const unreadCount = typeof oldData.unreadCount === "number" ? Math.max(0, oldData.unreadCount + (unread ? 1 : -1)) : oldData.unreadCount;
  return {
    ...oldData,
    unreadCount,
    conversations: oldData.conversations.map((c) =>
      c.conversationId === conversationId ? { ...c, unread, unreadMessages: unread ? Math.max(1, c.unreadMessages || 0) : 0 } : c
    ),
  };
}

// The "All" / "Unread" tabs for lists that are not paginated (the plan WhatsApp and plan email pages). A chat
// you open from the Unread tab stays listed (until you leave the tab or reload) instead of vanishing from
// under your cursor the moment it is marked read, the way WhatsApp's own Unread filter behaves.
export function filterConversationsByTab(conversations, tab, openedIds) {
  if (tab !== "unread") return conversations;
  return conversations.filter((c) => c.unread || (openedIds && openedIds.has(c.conversationId)));
}

// Search by name, phone number (digits only, so "+91 98116-29081" works), email address or Instagram username.
// For the plan pages, whose whole chat list is already loaded, so this filters in the browser.
export function filterConversationsBySearch(conversations, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return conversations;
  const digits = q.replace(/\D/g, "");
  return conversations.filter((c) => {
    const names = [
      c.customerName,
      c.influencerEmail,
      c.influencerDbId?.userName,
      c.influencerDbId?.username,
      c.rapidInfluencerId?.userName,
      c.rapidInfluencerId?.username,
    ];
    if (names.some((n) => n && String(n).toLowerCase().includes(q))) return true;
    return digits.length >= 3 && String(c.customerWaId || "").includes(digits);
  });
}

// The Unread tab is filtered by the server, so a chat you just opened (and so marked read) would drop out
// of the list on the very next refetch, right from under your cursor. Chats opened this visit are kept and
// merged back in — shown as read — until you leave the tab, the way WhatsApp's own Unread filter behaves. A
// server row for the same chat always wins (it means a new unread message arrived). Used by the WhatsApp
// Inbox and the Email Inbox.
export function mergeOpenedChats(serverConversations, openedById) {
  const have = new Set(serverConversations.map((c) => c.conversationId));
  const kept = [...openedById.values()].filter((c) => !have.has(c.conversationId));
  return sortConversationsNewestFirst([...serverConversations, ...kept]);
}

// A live message just arrived in `conv` (a row of the cached list): its new unread count. Our own
// message (a person's reply, the AI's, a template) answers the chat, so 0; a creator's message adds
// one to whatever was already waiting. Capped at 100 like the server ("99+" on screen).
export function unreadMessagesAfter(conv, message) {
  if (message?.isFromUs) return 0;
  const waiting = conv?.unread ? conv.unreadMessages ?? 1 : 0;
  return Math.min(waiting + 1, 100);
}

// One list row after a live message: new preview and time, and its unread state. Our own message
// answers the chat; a creator's message makes it unread until someone opens it. Shared by the Inbox
// and the plan WhatsApp page so both lists apply the exact same rule.
export function withLiveMessage(conv, message) {
  return {
    ...conv,
    lastMessageAt: message.sentAt,
    messages: [message],
    unread: !message.isFromUs,
    unreadMessages: unreadMessagesAfter(conv, message),
  };
}

// WhatsApp's green circle with the number of unread messages in a chat. Nothing at 0; "99+" above 99.
export function UnreadBadge({ count }) {
  if (!count || count < 1) return null;
  return (
    <Box
      component="span"
      aria-label={`${count} unread message${count === 1 ? "" : "s"}`}
      sx={{
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 99,
        bgcolor: "#25D366",
        color: "#fff",
        fontSize: 11,
        fontWeight: 800,
        fontFamily: "Quicksand",
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

// Search box above a chat list (name, number or username), shared by the plan WhatsApp page and the
// main Inbox. `onChange` gets the raw text; "" when cleared.
export function ChatSearchField({ value, onChange, placeholder = "Search name, number or username" }) {
  return (
    <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => onChange("")}>
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: { fontFamily: "Quicksand", fontSize: 13, borderRadius: "10px" },
        }}
      />
    </Box>
  );
}

// The "All" / "Unread" tabs above a chat list, shared by the plan WhatsApp page and the main Inbox.
export function ChatListTabs({ tab, onChange, allCount, unreadCount }) {
  return (
    <Tabs
      value={tab}
      onChange={(_, value) => onChange(value)}
      variant="fullWidth"
      sx={{
        minHeight: 42,
        flexShrink: 0,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        "& .MuiTabs-indicator": { backgroundColor: "#25D366", height: 3 },
        "& .MuiTab-root": { minHeight: 42, textTransform: "none", fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#6B7280" },
        "& .MuiTab-root.Mui-selected": { color: "#128C7E" },
      }}
    >
      <Tab value="all" label={`All${allCount ? ` (${allCount})` : ""}`} />
      <Tab
        value="unread"
        label={
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <span>Unread</span>
            {unreadCount > 0 && (
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  px: 0.6,
                  borderRadius: 99,
                  bgcolor: "#25D366",
                  color: "#fff",
                  fontSize: 10.5,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {unreadCount}
              </Box>
            )}
          </Stack>
        }
      />
    </Tabs>
  );
}

// AI follow-up (backend: helpers/whatsappAi.js). Only conversations that were started from a
// Plan bulk send ever have a status — everything else renders nothing here.
const AI_STATUS = {
  awaiting_reply: { short: "AI waiting", color: "#B36B00", bg: "rgba(250,153,5,0.14)" },
  in_progress: { short: "AI chatting", color: "#1D4ED8", bg: "rgba(59,130,246,0.12)" },
  complete: { short: "AI done", color: "#09AC31", bg: "rgba(9,172,49,0.12)" },
  // The AI no longer hands chats to a person; older "handoff" chats show like any chat the AI is handling.
  handoff: { short: "AI chatting", color: "#1D4ED8", bg: "rgba(59,130,246,0.12)" },
};

export function AiStatusChip({ status, paused }) {
  const meta = AI_STATUS[status];
  if (!meta) return null;
  // Finished chats are still answered by the AI, so a pause on one is worth showing too.
  const showPaused = Boolean(paused);
  // Nothing to say in the list until the AI is actually doing something (or has been paused).
  if (status === "awaiting_reply" && !showPaused) return null;
  return (
    <Chip
      size="small"
      icon={<SmartToyOutlinedIcon sx={{ fontSize: "12px !important", color: "inherit !important" }} />}
      label={showPaused ? "AI paused" : meta.short}
      sx={{
        height: 18,
        bgcolor: showPaused ? "#F1F1F1" : meta.bg,
        color: showPaused ? "#666" : meta.color,
        fontWeight: 700,
        fontSize: 10,
        flexShrink: 0,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

// Thread-header strip: what the AI has collected so far, what is still missing, and (when
// `onTogglePause` is given) the per-thread pause switch.
// `noun` is what the thread is called on this channel ("chat" for WhatsApp, "email" for email).
export function AiProgressBar({ progress, onTogglePause, pausing, noun = "chat" }) {
  // Re-render when the "teammate replied recently" window ends, so the waiting text clears by itself.
  const [, setTick] = useState(0);
  const skipUntilMs = progress?.skipUntil ? new Date(progress.skipUntil).getTime() : 0;
  useEffect(() => {
    const ms = skipUntilMs - Date.now();
    if (ms <= 0) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), ms + 500);
    return () => clearTimeout(t);
  }, [skipUntilMs]);
  // A reason with no skipUntil (e.g. no deliverables on the list) holds until the server stops sending it.
  const skipActive = Boolean(progress?.skipReason) && (!progress?.skipUntil || skipUntilMs > Date.now());

  if (!progress) return null;
  const meta = AI_STATUS[progress.status];
  if (!meta) return null;

  // The AI answers every plan chat, finished ones included, until it closes one itself (end_chat) —
  // so a paused chat reads as paused whatever its status, and only a closed chat can't be paused.
  const ended = Boolean(progress.endedReason);
  let summary;
  if (ended) summary = `AI closed this ${noun} — ${progress.endedReason}`;
  else if (progress.paused) summary = "AI paused — it won't reply until you turn Pause AI off";
  else if (progress.status === "complete") summary = `AI finished — ${progress.answered} detail${progress.answered === 1 ? "" : "s"} collected and saved`;
  else if (skipActive) summary = `AI waiting — ${progress.skipReason.charAt(0).toLowerCase()}${progress.skipReason.slice(1)}`;
  else if (progress.status === "handoff") summary = "AI will carry on when the creator replies";
  else if (progress.status === "awaiting_reply") summary = "AI will take over when the creator replies";
  else summary = `AI collecting details — ${progress.answered}/${progress.total} done`;

  const canPause = onTogglePause && !ended;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ px: 2.5, py: 0.75, bgcolor: meta.bg, borderBottom: "1px solid rgba(0,0,0,0.05)", flexShrink: 0 }}
    >
      <SmartToyOutlinedIcon sx={{ fontSize: 15, color: meta.color }} />
      <Tooltip
        title={progress.missing?.length ? `Still needed: ${progress.missing.join(" · ")}` : ""}
        placement="bottom-start"
        disableHoverListener={!progress.missing?.length}
      >
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, fontWeight: 700, color: meta.color, flex: 1, minWidth: 0 }}>
          {summary}
        </Typography>
      </Tooltip>
      {canPause && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Stack alignItems="flex-end" sx={{ lineHeight: 1.2 }}>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, fontWeight: 700, color: progress.paused ? "#E34C14" : "#16A34A" }}>
              Pause AI: {progress.paused ? "ON" : "OFF"}
            </Typography>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: "#6B7280" }}>
              {progress.paused ? "AI won't reply — you handle this chat" : "AI replies to the creator automatically"}
            </Typography>
          </Stack>
          <Switch size="small" checked={Boolean(progress.paused)} disabled={pausing} onChange={(e) => onTogglePause(e.target.checked)} />
        </Stack>
      )}
    </Stack>
  );
}

export function MessageBubble({ message, accentColor }) {
  const isUs = message.isFromUs;
  const isSending = message._status === "sending";
  const isFailed = message._status === "failed";
  return (
    <Box sx={{ display: "flex", justifyContent: isUs ? "flex-end" : "flex-start", mb: 0.75, opacity: isSending ? 0.6 : 1 }}>
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "70%", md: "60%" },
          background: isFailed
            ? "rgba(229,57,53,0.12)"
            : isUs
            ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)`
            : "rgba(255,255,255,0.97)",
          color: isFailed ? "#E53935" : isUs ? "#fff" : "#1C1C2E",
          borderRadius: isUs ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          px: 2,
          py: 1.25,
          boxShadow: isUs ? `0 4px 16px ${accentColor}40` : "0 2px 8px rgba(0,0,0,0.07)",
          border: isFailed ? "1px solid rgba(229,57,53,0.3)" : isUs ? "none" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {message.isAi && (
          <Typography sx={{ fontSize: 10, fontWeight: 800, fontFamily: "Quicksand", opacity: 0.75, mb: 0.25, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 0.4 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 11 }} /> AI
          </Typography>
        )}
        {message.type === "template" && (
          <Typography sx={{ fontSize: 10, fontWeight: 800, fontFamily: "Quicksand", opacity: 0.75, mb: 0.25, letterSpacing: 0.3 }}>
            TEMPLATE{message.templateName ? ` · ${message.templateName}` : ""}
          </Typography>
        )}
        {message.text ? (
          <Typography sx={{ fontSize: 13.5, fontFamily: "Quicksand", fontWeight: 600, lineHeight: 1.55, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
            {message.text}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", fontStyle: "italic", opacity: 0.55 }}>
            [{message.type || "attachment"}]
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.4, mt: 0.5 }}>
          <Typography sx={{ fontSize: 10, fontFamily: "Quicksand", color: isUs ? "rgba(255,255,255,0.65)" : "#9CA3AF" }}>
            {formatTime(message.sentAt)}
          </Typography>
          {isUs && !isFailed && !isSending && <StatusTick status={message.status} />}
        </Box>
      </Box>
    </Box>
  );
}
